import React from 'react';
import PropTypes from 'prop-types';
import { TextInput } from '@els/els-react--text-input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { SingleSelect } from '@els/els-react--select';
import HoverTooltip from '@Components/HoverTooltip';
import LoginFormHeader from '../../Components/LoginFormHeader';

import './RegisterFinalizeForm.scss';
import '../../Components/LoginForm.scss';

function RegisterFinalizeForm({ ipAddress, isLoading, submit, skip, data, marketingInfo, setData, registerFinalizeFail }) {
  const spinnerId = 'finalize-register-page';

  const mapDropdownItems = (items) => {
    if (!items || !items.length) {
      return [];
    }
    return items.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  };

  const mappedUserAERoles = mapDropdownItems(marketingInfo.userAERoles);
  const mappedSubjectAreas = mapDropdownItems(marketingInfo.subjectAreas);
  const mappedOrganizationTypes = mapDropdownItems(marketingInfo.organizationTypes);

  const submitHandler = () => {
    if (!data.organizationType || !data.subjectAreas || !data.userRole) {
      return;
    }
    submit();
  };

  const onChangeOrganizationName = (event) => {
    setData({
      organizationName: event.target.value,
    });
  };

  const onSelectUserRole = (option) => {
    if (option) setData({ userRole: option.value });
  };

  const onSelectSubjectArea = (option) => {
    if (option) setData({ subjectAreas: option.value });
  };

  const onSelectOrganizationType = (option) => {
    if (option) setData({ organizationType: option.value });
  };

  const tooltipContent =
    '<div class="finalize-register-info-tooltip"><p>You\'ve agreed to receive emails about our product. To ensure we send information relevant to your business, interests, and job title, please provide the following details.</p></div>';

  return (
    <div className="login-form login-page-form-wrapper register-finalize-form">
      <LoginFormHeader
        ipAddress={ipAddress}
        actionMessage="Professional information"
        infoMessage={
          <span className="finalize-register-info-message">
            Add your professional information{' '}
            <HoverTooltip tooltip={tooltipContent}>
              <ButtonWithIcon
                type="link"
                linkIconSize="xs"
                sprite="InformationOutlineCircle"
                iconOnly
                data-e2e="finalize-register-learn-more-button">
                info
              </ButtonWithIcon>
            </HoverTooltip>
          </span>
        }
      />
      <form name="registerFinalizeForm">
        <div className="login-form__content">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          <div className="finalize-register-name__dropdown u-els-margin-top">
            <SingleSelect
              id="finalize-register-userRole-dropdown"
              className="e2e-finalize-register-userRole-dropdown"
              options={mappedUserAERoles}
              data-e2e="finalize-register-userRole-dropdown"
              selectedOptions={data.userRole ? [data.userRole] : undefined}
              label="Job title"
              onSelect={onSelectUserRole}
              size="small"
            />
          </div>

          <div className="finalize-register-name__dropdown u-els-margin-top">
            <SingleSelect
              id="finalize-register-subjectArea-dropdown"
              className="e2e-finalize-register-subjectArea-dropdown"
              options={mappedSubjectAreas}
              data-e2e="finalize-register-subjectArea-dropdown"
              selectedOptions={data.subjectAreas ? [data.subjectAreas] : undefined}
              label="Your subject area"
              onSelect={onSelectSubjectArea}
              size="small"
            />
          </div>

          <div className="els-text-input u-els-float-none u-els-padding-top">
            <TextInput
              id="finalize-register-organization-name"
              data-e2e="finalize-register-organization-name-input"
              autoComplete="organization"
              size="small"
              name="organizationName"
              label="Organization name"
              value={typeof data.organizationName === 'string' ? data.organizationName : ''}
              onChange={onChangeOrganizationName}
            />
          </div>

          <div className="finalize-register-name__dropdown u-els-margin-top">
            <SingleSelect
              id="finalize-register-organizationType-dropdown"
              className="e2e-finalize-register-organizationType-dropdown"
              options={mappedOrganizationTypes}
              selectedOptions={data.organizationType ? [data.organizationType] : undefined}
              label="Organization type"
              data-e2e="finalize-register-organizationType-dropdown"
              onSelect={onSelectOrganizationType}
              size="small"
            />
          </div>
          {registerFinalizeFail && <div className="login-form__content_error u-els-margin-top">{registerFinalizeFail}</div>}

          <div className="login-form__content_actions">
            <div className="login-form__content_actions-button u-els-padding-top-1x1o2">
              <Button
                type="primary"
                data-e2e="finalize-register-submit-button"
                disabled={isLoading}
                onClick={submitHandler}
                expanded
                className="e2e-login-sign-in">
                Save
              </Button>
            </div>
            <div className="login-form__content_actions-button u-els-padding-top-1x1o2">
              <Button type="tertiary" data-e2e="finalize-register-skip-button" disabled={isLoading} onClick={skip} expanded>
                Skip
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

RegisterFinalizeForm.propTypes = {
  data: PropTypes.shape({
    subjectAreas: PropTypes.string,
    userRole: PropTypes.string,
    organizationType: PropTypes.string,
    organizationName: PropTypes.string,
  }),
  marketingInfo: PropTypes.shape({
    organizationTypes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
    subjectAreas: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
    userAERoles: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  }),
  setData: PropTypes.func,
  ipAddress: PropTypes.string,
  registerFinalizeFail: PropTypes.string,
  isLoading: PropTypes.bool,
  submit: PropTypes.func.isRequired,
  skip: PropTypes.func,
};

export default RegisterFinalizeForm;
